import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { clinicalAnalyticsActions } from 'store/clinical';
import { InsightsDonutChart } from './components/apexDonutChart';
import { goalStatusColors, domainColors } from './constants';
import { Images } from 'utils';
import moment from 'moment';

const FONT_FAMILY = '"GT Walsheim Pro", "Open Sans", sans-serif';
const AXIS_NAME_TEXT_STYLE = {
    color: '#475467',
    fontSize: 12,
    fontWeight: 600,
    fontFamily: FONT_FAMILY,
};

const triggerDownload = (url, fileName) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const loadImage = (src) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

const roundRect = (context, x, y, width, height, radius) => {
    context.beginPath();
    context.moveTo(x + radius, y);
    context.lineTo(x + width - radius, y);
    context.quadraticCurveTo(x + width, y, x + width, y + radius);
    context.lineTo(x + width, y + height - radius);
    context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    context.lineTo(x + radius, y + height);
    context.quadraticCurveTo(x, y + height, x, y + height - radius);
    context.lineTo(x, y + radius);
    context.quadraticCurveTo(x, y, x + radius, y);
    context.closePath();
};

const createExportCanvas = (width, height) => {
    const scale = window.devicePixelRatio > 1 ? 2 : 1;
    const canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = height * scale;
    const context = canvas.getContext('2d');

    if (!context) {
        throw new Error('Canvas context not available');
    }

    context.scale(scale, scale);
    context.fillStyle = '#FFFFFF';
    context.fillRect(0, 0, width, height);

    return { canvas, context };
};

const exportDonutCard = async ({ chartRef, title, labels, values, colors, fileName }) => {
    const chartInstance = chartRef?.current?.getEchartsInstance?.();
    if (!chartInstance) {
        return;
    }

    const chartImageUrl = chartInstance.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#FFFFFF',
        excludeComponents: ['toolbox'],
    });
    const chartImage = await loadImage(chartImageUrl);

    const width = 720;
    const height = 340;
    const { canvas, context } = createExportCanvas(width, height);

    roundRect(context, 0.5, 0.5, width - 1, height - 1, 12);
    context.fillStyle = '#FFFFFF';
    context.fill();
    context.strokeStyle = '#EAECF0';
    context.stroke();

    context.fillStyle = '#1D2939';
    context.font = `600 15px ${FONT_FAMILY}`;
    context.fillText(title, 20, 30);

    context.drawImage(chartImage, 24, 56, 220, 220);

    const legendX = 276;
    let legendY = 58;

    labels.forEach((label, index) => {
        roundRect(context, legendX, legendY, 420, 36, 10);
        context.fillStyle = '#FCFCFD';
        context.fill();
        context.strokeStyle = '#EAECF0';
        context.stroke();

        context.beginPath();
        context.arc(292, legendY + 18, 5, 0, Math.PI * 2);
        context.fillStyle = colors[index];
        context.fill();

        context.fillStyle = '#344054';
        context.font = `500 13px ${FONT_FAMILY}`;
        context.fillText(label, 306, legendY + 22);

        context.fillStyle = '#1D2939';
        context.font = `700 15px ${FONT_FAMILY}`;
        const valueText = `${values[index] || 0}`;
        const valueWidth = context.measureText(valueText).width;
        context.fillText(valueText, 682 - valueWidth, legendY + 22);

        legendY += 46;
    });

    triggerDownload(canvas.toDataURL('image/png'), fileName);
};

const SummaryCard = ({ icon, iconBg, label, value, sub }) => (
    <div className="summary-card">
        <div className="summary-card-icon" style={{ background: iconBg }}>
            {icon}
        </div>
        <div className="summary-card-info">
            <p className="summary-card-label">{label}</p>
            <p className="summary-card-value">{value ?? 0}</p>
            {sub && <p className="summary-card-sub">{sub}</p>}
        </div>
    </div>
);

export const OverviewTab = ({ clientId }) => {
    const dispatch = useDispatch();
    const overview = useSelector((state) => state.clinicalAnalytics.clientOverview);
    const domainProgress = useSelector((state) => state.clinicalAnalytics.domainProgress);
    const [domainFilter, setDomainFilter] = useState('');
    const statusDonutChartRef = useRef(null);
    const domainDonutChartRef = useRef(null);
    const goalMasteryChartRef = useRef(null);
    const domainProgressChartRef = useRef(null);
    const domainStatusChartRef = useRef(null);

    useEffect(() => {
        if (clientId) {
            const startDate = moment().subtract(9, 'months').startOf('month').format('YYYY-MM-DD');
            const endDate = moment().format('YYYY-MM-DD');
            dispatch(clinicalAnalyticsActions.getDomainProgress(clientId, { startDate, endDate, domain: domainFilter || undefined }));
        }
    }, [clientId, domainFilter]);

    if (!overview) {
        return (
            <div className="insights-loading">
                <CircularProgress size={32} />
            </div>
        );
    }

    const { summary, goalDistribution } = overview;

    // Donut data
    const statusSeries = goalDistribution?.byStatus?.map((d) => d.value) || [];
    const statusLabels = goalDistribution?.byStatus?.map((d) => d.label) || [];
    const statusDonutColors = goalDistribution?.byStatus?.map((d) => goalStatusColors[d.label] || '#D0D5DD') || [];

    const domainSeries = goalDistribution?.byDomain?.map((d) => d.value) || [];
    const domainLabels = goalDistribution?.byDomain?.map((d) => d.label) || [];
    const domainDonutColors = goalDistribution?.byDomain?.map((_, i) => domainColors[i % domainColors.length]) || [];

    // Domain status breakdown
    const domainStatusData = domainProgress?.domainStatusBreakdown || [];

    const handleDownloadChart = (chartRef, prefix) => {
        const chartInstance = chartRef?.current?.getEchartsInstance?.();
        if (!chartInstance) {
            return;
        }

        const imageUrl = chartInstance.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#FFFFFF',
            excludeComponents: ['toolbox'],
        });

        triggerDownload(imageUrl, `${prefix}-${clientId || 'client'}-${moment().format('YYYYMMDD-HHmmss')}.png`);
    };

    const handleDownloadDonutCard = async ({ chartRef, title, labels, values, colors, prefix }) => {
        try {
            await exportDonutCard({
                chartRef,
                title,
                labels,
                values,
                colors,
                fileName: `${prefix}-${clientId || 'client'}-${moment().format('YYYYMMDD-HHmmss')}.png`,
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to export overview donut chart', error);
        }
    };

    return (
        <div>
            {/* Summary KPI Cards */}
            <p className="insights-section-label">Summary</p>
            <div className="summary-cards-grid">
                <SummaryCard icon="&#9678;" iconBg="#EFF4FF" label="Total Goals" value={summary.totalGoals} />
                <SummaryCard
                    icon="&#10003;"
                    iconBg="#ECFDF3"
                    label="Mastered Goals"
                    value={summary.masteredGoals}
                    sub={summary.masteredThisMonth > 0 ? `+${summary.masteredThisMonth} this month` : null}
                />
                <SummaryCard icon="&#9733;" iconBg="#FFF6ED" label="Active Goals" value={summary.activeGoals} />
                <SummaryCard icon="&#9679;" iconBg="#F2F4F7" label="On Hold" value={summary.onHoldGoals} />
                <SummaryCard icon="&#128197;" iconBg="#EFF4FF" label="Sessions This Month" value={summary.sessionsThisMonth} />
                <SummaryCard
                    icon="&#9776;"
                    iconBg="#ECFDF3"
                    label="Total Trials Logged"
                    value={summary.totalTrialsLogged}
                    sub={summary.trialsThisWeek > 0 ? `+${summary.trialsThisWeek} this week` : null}
                />
            </div>

            {/* Goal Distribution Donuts */}
            <p className="insights-section-label">Goal Distribution</p>
            <div className="donut-charts-row">
                <div className="donut-chart-wrapper">
                    <div className="insights-chart-card-header insights-chart-card-header-tight">
                        <p className="donut-chart-title">Goals by Status</p>
                        <button
                            className="insights-export-btn insights-export-btn-inline"
                            onClick={() =>
                                handleDownloadDonutCard({
                                    chartRef: statusDonutChartRef,
                                    title: 'Goals by Status',
                                    labels: statusLabels,
                                    values: statusSeries,
                                    colors: statusDonutColors,
                                    prefix: 'overview-goals-by-status',
                                })
                            }
                        >
                            <img src={Images.downloadCloudBlue} alt="download" className="insights-export-icon" />
                        </button>
                    </div>
                    <InsightsDonutChart
                        chartRef={statusDonutChartRef}
                        series={statusSeries}
                        labels={statusLabels}
                        colors={statusDonutColors}
                    />
                    <div className="donut-legend">
                        {goalDistribution?.byStatus?.map((item, idx) => (
                            <div key={idx} className="donut-legend-item">
                                <div className="donut-legend-left">
                                    <span className="donut-legend-dot" style={{ background: statusDonutColors[idx] }} />
                                    <span>{item.label}</span>
                                </div>
                                <span className="donut-legend-value">{item.value}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="donut-chart-wrapper">
                    <div className="insights-chart-card-header insights-chart-card-header-tight">
                        <p className="donut-chart-title">Goals by Domain</p>
                        <button
                            className="insights-export-btn insights-export-btn-inline"
                            onClick={() =>
                                handleDownloadDonutCard({
                                    chartRef: domainDonutChartRef,
                                    title: 'Goals by Domain',
                                    labels: domainLabels,
                                    values: domainSeries,
                                    colors: domainDonutColors,
                                    prefix: 'overview-goals-by-domain',
                                })
                            }
                        >
                            <img src={Images.downloadCloudBlue} alt="download" className="insights-export-icon" />
                        </button>
                    </div>
                    <InsightsDonutChart
                        chartRef={domainDonutChartRef}
                        series={domainSeries}
                        labels={domainLabels}
                        colors={domainDonutColors}
                    />
                    <div className="donut-legend">
                        {goalDistribution?.byDomain?.map((item, idx) => (
                            <div key={idx} className="donut-legend-item">
                                <div className="donut-legend-left">
                                    <span className="donut-legend-dot" style={{ background: domainDonutColors[idx] }} />
                                    <span>{item.label}</span>
                                </div>
                                <span className="donut-legend-value">{item.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Goals Mastered Over Time */}
            <GoalMasteryOverTimeChart
                chartRef={goalMasteryChartRef}
                data={domainProgress?.goalMasteryOverTime || []}
                onDownload={() => handleDownloadChart(goalMasteryChartRef, 'overview-goals-mastered-over-time')}
            />

            {/* Domain Progress Over Time — ECharts line */}
            <div className="insights-chart-card">
                <div className="insights-chart-card-header">
                    <div>
                        <p className="insights-chart-card-title">Domain Progress Over Time</p>
                        <p style={{ fontSize: 12, color: '#667085', margin: '4px 0 0', fontFamily: FONT_FAMILY }}>
                            Mastery % by clinical domain
                        </p>
                    </div>
                    <select className="insights-domain-select" value={domainFilter} onChange={(e) => setDomainFilter(e.target.value)}>
                        <option value="">All Domains</option>
                        {goalDistribution?.byDomain?.map((d, i) => (
                            <option key={d.id || i} value={d.id || ''}>
                                {d.label}
                            </option>
                        ))}
                    </select>
                    <button
                        className="insights-export-btn insights-export-btn-inline"
                        onClick={() => handleDownloadChart(domainProgressChartRef, 'overview-domain-progress')}
                    >
                        <img src={Images.downloadCloudBlue} alt="download" className="insights-export-icon" />
                    </button>
                </div>
                <DomainProgressLineChart chartRef={domainProgressChartRef} data={domainProgress?.domainProgress || []} />
            </div>

            {/* Domain Progress by Status — ECharts stacked bar */}
            {domainStatusData.length > 0 && (
                <div className="insights-chart-card">
                    <div className="insights-chart-card-header insights-chart-card-header-tight">
                        <div>
                            <p className="insights-chart-card-title">Domain Progress by Status</p>
                            <p style={{ fontSize: 12, color: '#667085', margin: '4px 0 0', fontFamily: FONT_FAMILY }}>
                                Goal count per domain, broken down by status
                            </p>
                        </div>
                        <button
                            className="insights-export-btn insights-export-btn-inline"
                            onClick={() => handleDownloadChart(domainStatusChartRef, 'overview-domain-status')}
                        >
                            <img src={Images.downloadCloudBlue} alt="download" className="insights-export-icon" />
                        </button>
                    </div>
                    <DomainStatusBarChart chartRef={domainStatusChartRef} data={domainStatusData} />
                </div>
            )}
        </div>
    );
};

/** Domain Progress Over Time — ECharts multi-line */
const DomainProgressLineChart = ({ chartRef, data }) => {
    const option = useMemo(() => {
        if (!data?.length) return null;

        // All domains share the same month categories
        const categories = data[0]?.data?.map((p) => p.date) || [];

        const series = data.map((dp, i) => ({
            name: dp.domain,
            type: 'line',
            smooth: true,
            data: dp.data?.map((p) => p.masteryPercent) || [],
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: { width: 2, color: domainColors[i % domainColors.length] },
            itemStyle: { color: domainColors[i % domainColors.length] },
        }));

        return {
            tooltip: {
                trigger: 'axis',
                textStyle: { fontFamily: FONT_FAMILY, fontSize: 12 },
                formatter: (params) => {
                    if (!params?.length) return '';
                    const month = moment(params[0].axisValue, 'YYYY-MM').format('MMM YYYY');
                    let html = `<b style="font-family:${FONT_FAMILY}">${month}</b>`;
                    params.forEach((p) => {
                        html += `<br/><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color};margin-right:6px;"></span>${p.seriesName}: <b>${p.value}%</b>`;
                    });
                    return html;
                },
            },
            legend: {
                bottom: 0,
                left: 'center',
                itemWidth: 10,
                itemHeight: 10,
                itemGap: 14,
                textStyle: { fontFamily: FONT_FAMILY, fontSize: 11, color: '#667085' },
            },
            grid: { left: 72, right: 24, bottom: 110, top: 24, containLabel: true },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: categories,
                name: 'Time',
                nameLocation: 'middle',
                nameGap: 34,
                nameTextStyle: AXIS_NAME_TEXT_STYLE,
                axisLine: { lineStyle: { color: '#E5E7EB' } },
                axisLabel: {
                    color: '#626F86',
                    fontSize: 12,
                    fontFamily: FONT_FAMILY,
                    formatter: (v) => moment(v, 'YYYY-MM').format('MMM'),
                },
            },
            yAxis: {
                type: 'value',
                min: 0,
                max: 100,
                name: 'Mastery Rate (%)',
                nameLocation: 'middle',
                nameGap: 52,
                nameRotate: 90,
                nameTextStyle: AXIS_NAME_TEXT_STYLE,
                axisLine: { show: false },
                axisLabel: { color: '#626F86', fontSize: 12, fontFamily: FONT_FAMILY, formatter: '{value}%' },
                splitLine: { lineStyle: { color: '#F3F4F6' } },
            },
            series,
        };
    }, [data]);

    if (!option) {
        return (
            <div className="insights-empty">
                <p>No domain progress data available</p>
            </div>
        );
    }

    return (
        <ReactECharts ref={chartRef} option={option} notMerge={true} style={{ height: 420, width: '100%' }} opts={{ renderer: 'canvas' }} />
    );
};

/** Domain Status Breakdown — ECharts stacked bar */
const DomainStatusBarChart = ({ chartRef, data }) => {
    const option = useMemo(() => {
        if (!data?.length) return null;

        const categories = data.map((d) => d.domain);
        const statuses = [
            { key: 'active', label: 'Active', color: goalStatusColors.ACTIVE },
            { key: 'mastered', label: 'Mastered', color: goalStatusColors.MASTERED },
            { key: 'pending', label: 'Pending', color: goalStatusColors.PENDING },
            { key: 'hold', label: 'Hold', color: goalStatusColors.HOLD },
            { key: 'discontinued', label: 'Discontinued', color: goalStatusColors.DISCONTINUED },
        ];

        const series = statuses.map((s) => ({
            name: s.label,
            type: 'bar',
            stack: 'total',
            data: data.map((d) => d[s.key] ?? 0),
            itemStyle: { color: s.color, borderRadius: [0, 0, 0, 0] },
            barMaxWidth: 40,
        }));

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                textStyle: { fontFamily: FONT_FAMILY, fontSize: 12 },
                formatter: (params) => {
                    if (!params?.length) return '';
                    const domain = params[0].axisValue;
                    const total = params.reduce((sum, p) => sum + (p.value || 0), 0);
                    let html = `<b style="font-family:${FONT_FAMILY}">${domain}</b>`;
                    params.forEach((p) => {
                        if (p.value > 0) {
                            html += `<br/><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color};margin-right:6px;"></span>${p.seriesName}: <b>${p.value}</b>`;
                        }
                    });
                    html += `<br/><span style="color:#667085">Total: <b>${total}</b></span>`;
                    return html;
                },
            },
            legend: {
                bottom: 0,
                textStyle: { fontFamily: FONT_FAMILY, fontSize: 12, color: '#667085' },
            },
            grid: { left: 72, right: 24, bottom: 90, top: 24, containLabel: true },
            xAxis: {
                type: 'category',
                data: categories,
                name: 'Clinical Domains',
                nameLocation: 'middle',
                nameGap: 34,
                nameTextStyle: AXIS_NAME_TEXT_STYLE,
                axisLine: { lineStyle: { color: '#E5E7EB' } },
                axisLabel: { color: '#626F86', fontSize: 12, fontFamily: FONT_FAMILY },
            },
            yAxis: {
                type: 'value',
                name: 'Goal Count',
                nameLocation: 'middle',
                nameGap: 52,
                nameRotate: 90,
                nameTextStyle: AXIS_NAME_TEXT_STYLE,
                axisLine: { show: false },
                axisLabel: { color: '#626F86', fontSize: 12, fontFamily: FONT_FAMILY },
                splitLine: { lineStyle: { color: '#F3F4F6' } },
            },
            series,
        };
    }, [data]);

    if (!option) return null;

    return (
        <ReactECharts ref={chartRef} option={option} notMerge={true} style={{ height: 300, width: '100%' }} opts={{ renderer: 'canvas' }} />
    );
};

/** Goals Mastered Over Time — line chart showing mastered count vs total */
const GoalMasteryOverTimeChart = ({ chartRef, data, onDownload }) => {
    const option = useMemo(() => {
        if (!data?.length) return null;

        const categories = data.map((p) => p.date);
        const masteredSeries = data.map((p) => p.masteredCount);
        const totalGoals = data[0]?.totalGoals ?? 0;

        return {
            tooltip: {
                trigger: 'axis',
                textStyle: { fontFamily: FONT_FAMILY, fontSize: 12 },
                formatter: (params) => {
                    if (!params?.length) return '';
                    const month = moment(params[0].axisValue, 'YYYY-MM').format('MMM YYYY');
                    const val = params[0].value;
                    return `<b style="color:#0C66E4">${month}</b><br/>Mastered: <b>${val}</b> / ${totalGoals} goals`;
                },
            },
            legend: { show: false },
            grid: { left: 72, right: 24, bottom: 72, top: 24, containLabel: true },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: categories,
                name: 'Time',
                nameLocation: 'middle',
                nameGap: 34,
                nameTextStyle: AXIS_NAME_TEXT_STYLE,
                axisLine: { lineStyle: { color: '#E5E7EB' } },
                axisLabel: {
                    color: '#626F86',
                    fontSize: 12,
                    fontFamily: FONT_FAMILY,
                    formatter: (v) => moment(v, 'YYYY-MM').format('MMM'),
                },
            },
            yAxis: {
                type: 'value',
                min: 0,
                max: totalGoals > 0 ? totalGoals : undefined,
                name: 'Mastered Goals',
                nameLocation: 'middle',
                nameGap: 52,
                nameRotate: 90,
                nameTextStyle: AXIS_NAME_TEXT_STYLE,
                axisLine: { show: false },
                axisLabel: { color: '#626F86', fontSize: 12, fontFamily: FONT_FAMILY },
                splitLine: { lineStyle: { color: '#F3F4F6' } },
            },
            series: [
                {
                    name: 'Mastered Goals',
                    type: 'line',
                    data: masteredSeries,
                    smooth: true,
                    symbol: 'circle',
                    symbolSize: 6,
                    lineStyle: { color: '#00C48C', width: 2.5 },
                    itemStyle: { color: '#00C48C', borderColor: '#fff', borderWidth: 2 },
                    areaStyle: {
                        color: {
                            type: 'linear',
                            x: 0,
                            y: 0,
                            x2: 0,
                            y2: 1,
                            colorStops: [
                                { offset: 0, color: 'rgba(0,196,140,0.12)' },
                                { offset: 1, color: 'rgba(0,196,140,0.01)' },
                            ],
                        },
                    },
                    markLine: {
                        symbol: 'none',
                        data: [
                            {
                                yAxis: totalGoals,
                                label: {
                                    formatter: `Total Goals (${totalGoals})`,
                                    position: 'insideEndTop',
                                    fontSize: 10,
                                    color: '#98A2B3',
                                    fontFamily: FONT_FAMILY,
                                },
                                lineStyle: { color: '#D0D5DD', type: 'dashed', width: 1 },
                            },
                        ],
                    },
                },
            ],
        };
    }, [data]);

    if (!option) return null;

    return (
        <div className="insights-chart-card">
            <div className="insights-chart-card-header insights-chart-card-header-tight">
                <div>
                    <p className="insights-chart-card-title">Goals Mastered Over Time</p>
                    <p style={{ fontSize: 12, color: '#667085', margin: '4px 0 0', fontFamily: FONT_FAMILY }}>
                        Number of mastered goals out of total
                    </p>
                </div>
                <button className="insights-export-btn insights-export-btn-inline" onClick={onDownload}>
                    <img src={Images.downloadCloudBlue} alt="download" className="insights-export-icon" />
                </button>
            </div>
            <ReactECharts
                ref={chartRef}
                option={option}
                notMerge={true}
                style={{ height: 280, width: '100%' }}
                opts={{ renderer: 'canvas' }}
            />
        </div>
    );
};
